// src/components/inventory/CurrentStats.tsx 
import React from 'react'; 
import { Package, Hash, User, Calendar } from 'lucide-react'; 
import LoadingSpinner from '../common/LoadingSpinner'; 
import { GetItemsData, InventoryItem } from '../../types/inventory'; 
 
interface CurrentStatsProps { 
  tagCode: string; 
  upc?: string; 
  batchId?: string;  // Only need batchId now
  inventoryData: GetItemsData | undefined; 
  isLoading: boolean; 
} 
 
const CurrentStats: React.FC<CurrentStatsProps> = ({  
  tagCode,  
  upc = '',  
  batchId,
  inventoryData,  
  isLoading  
}) => { 
  // Safe helper function to normalize strings 
  const normalizeString = (str: string | null | undefined): string => { 
    return (str || '').trim().toLowerCase(); 
  }; 

  // Extract info from batchId (new readable format)
  const extractNameFromBatchId = (batchId: string | undefined): string => {
    if (!batchId) return '';
    const parts = batchId.split('_');
    return parts[0] || '';
  };

  const extractDateTimeFromBatchId = (batchId: string | undefined): { date: string; time: string } => {
    if (!batchId) return { date: '', time: '' };
    
    const parts = batchId.split('_');
    if (parts.length < 3) return { date: '', time: '' };
    
    const datePart = parts[1]; // YYYYMMDD
    const timePart = parts[2]; // HHMMSS
    
    if (datePart.length === 8 && timePart.length >= 6) {
      const year = datePart.substring(0, 4);
      const month = datePart.substring(4, 6);
      const day = datePart.substring(6, 8);
      
      const hours = timePart.substring(0, 2);
      const minutes = timePart.substring(2, 4);
      const seconds = timePart.substring(4, 6);
      
      const date = `${month}/${day}/${year}`;
      const time = `${hours}:${minutes}:${seconds}`;
      
      return { date, time };
    }
    
    return { date: '', time: '' };
  };

  // Format session start time for display - readable format
  const formatSessionTime = (): string => {
    const { date, time } = extractDateTimeFromBatchId(batchId);
    if (!date || !time) return '';
    return `${date}, ${time}`;
  };

  const userName = extractNameFromBatchId(batchId);
 
  const matchedItem = inventoryData?.items?.find( 
    (item: InventoryItem) => { 
      const itemTagCode = normalizeString(item.tagCode); 
      const itemUpc = normalizeString(item.upc); 
      const searchTagCode = normalizeString(tagCode); 
      const searchUpc = normalizeString(upc); 
       
      return itemTagCode === searchTagCode && itemUpc === searchUpc; 
    } 
  ); 
 
  if (isLoading) { 
    return ( 
      <div className="bg-white rounded-lg shadow-sm border p-6"> 
        <LoadingSpinner size="sm" text="Loading stats..." /> 
      </div> 
    ); 
  } 
 
  return ( 
    <div className="bg-white rounded-lg shadow-sm border p-6"> 
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center"> 
        <Package className="h-5 w-5 mr-2 text-blue-600" /> 
        Current Stats 
      </h3> 
 
      <div className="space-y-3"> 
        {/* Session Info */}
        <div className="bg-blue-50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700 flex items-center">
              <User className="h-4 w-4 mr-1" />
              Scanner:
            </span>
            <span className="text-sm text-blue-900 font-semibold">{userName || 'N/A'}</span>
          </div>
          
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700 flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              Session Started:
            </span>
            <span className="text-xs text-blue-800">{formatSessionTime() || 'N/A'}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-700">Batch ID:</span>
            <span className="text-xs text-blue-900 font-mono bg-blue-100 px-2 py-1 rounded">
              {batchId || 'N/A'}
            </span>
          </div>
        </div>

        {/* Current Item Info */}
        <div className="flex items-center justify-between"> 
          <span className="text-sm font-medium text-gray-700">Tag Code:</span> 
          <span className="text-sm text-gray-900 font-mono">{tagCode}</span> 
        </div> 
 
        <div className="flex items-center justify-between"> 
          <span className="text-sm font-medium text-gray-700">UPC:</span> 
          <span className="text-sm text-gray-900 font-mono">{upc || '-'}</span> 
        </div> 
 
        <div className="flex items-center justify-between"> 
          <span className="text-sm font-medium text-gray-700">Count:</span> 
          <div className="flex items-center"> 
            <Hash className="h-4 w-4 mr-1 text-green-600" /> 
            <span className="text-lg font-bold text-green-600"> 
              {matchedItem?.count ?? '-'} 
            </span> 
          </div> 
        </div>

        {/* Additional Stats */}
        <div className="border-t pt-3 mt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total Items:</span>
            <span className="text-sm text-gray-900 font-semibold">
              {inventoryData?.items?.length || 0}
            </span>
          </div>
        </div>
      </div> 
    </div> 
  ); 
}; 
 
export default CurrentStats;